import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { MapPin } from 'lucide-react'
import { Reveal } from '@/components/generals/reveal'
import { Eyebrow } from '@/components/generals/eyebrow'
import { areaHighlights } from '@/constants/estate'

/*
   Location
   Introduction and address card beside a landscape photograph, then four
   amenity cards (medical, schools, shopping, outdoors) in a 4 / 2 / 1 grid.
*/
export const LocationSection: FC = (): ReactNode => {
    return (
        <section id='location' aria-labelledby='location-heading' className='bg-cream py-24 lg:py-32'>
            <div className='mx-auto max-w-7xl px-6 lg:px-10'>
                <div className='grid gap-12 lg:grid-cols-12 lg:gap-16'>
                    <Reveal className='lg:col-span-5 lg:pt-6'>
                        <Eyebrow>
                            The neighbourhood
                        </Eyebrow>

                        <h2 id='location-heading' className='mt-6 font-heading text-4xl leading-[1.08] sm:text-5xl'>
                            Central Hillcrest, <em className='italic text-forest'>at your door</em>
                        </h2>

                        <p className='mt-6 text-[1.0625rem] leading-relaxed text-muted-foreground'>
                            Rockwood sits in the Central and Hilldene area of Hillcrest, the village heart of the Upper Highway. Hospitals, schools and shops are minutes away, and Durban and the coast are an easy drive along the M13.
                        </p>

                        <div className='mt-10 flex items-start gap-4 rounded-2xl border border-stone bg-sand p-6'>
                            <MapPin aria-hidden='true' className='mt-0.5 size-5 shrink-0 text-forest' />

                            <div>
                                <p className='text-sm font-medium'>
                                    Central Hillcrest and Hilldene
                                </p>

                                <p className='mt-1 text-sm leading-relaxed text-muted-foreground'>
                                    Close to Old Main Road, with quick access to the M13 and N3.
                                </p>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={120} className='lg:col-span-7'>
                        <div className='relative aspect-[4/3] overflow-hidden rounded-3xl bg-forest-soft lg:aspect-[5/4]'>
                            <Image
                                src='/images/hillcrest-valley.jpg'
                                alt='Rolling green hills and misty valleys, typical of the countryside around Hillcrest'
                                fill
                                sizes='(min-width: 1024px) 55vw, 100vw'
                                className='object-cover object-bottom'
                            />

                            <div className='absolute inset-x-0 bottom-0 bg-gradient-to-t from-charcoal/65 to-transparent p-6 pt-16 text-cream sm:p-8 sm:pt-20'>
                                <p className='text-[0.6875rem] uppercase tracking-[0.3em] text-cream/80'>
                                    The Upper Highway
                                </p>

                                <p className='mt-2 font-heading text-2xl sm:text-3xl'>
                                    Green hills, cool mist and big skies
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>

                {/* Area highlights */}
                <ul className='mt-16 grid gap-4 sm:grid-cols-2 lg:mt-20 lg:grid-cols-4'>
                    {areaHighlights.map((area, index) => {
                        const Icon = area.icon

                        return (
                            <li key={area.title}>
                                <Reveal delay={index * 80} className='h-full'>
                                    <article className='h-full rounded-2xl border border-stone bg-cream p-7 transition-[transform,border-color] duration-500 ease-out hover:-translate-y-1 hover:border-forest/30'>
                                        <div className='flex items-center justify-between'>
                                            <span className='inline-flex size-12 items-center justify-center rounded-full bg-forest-soft text-forest'>
                                                <Icon aria-hidden='true' className='size-5' />
                                            </span>

                                            <span aria-hidden='true' className='font-heading text-sm italic text-muted-foreground'>
                                                {`0${index + 1}`}
                                            </span>
                                        </div>

                                        <h3 className='mt-6 font-heading text-xl'>
                                            {area.title}
                                        </h3>

                                        <p className='mt-2 text-sm text-muted-foreground'>
                                            {area.summary}
                                        </p>

                                        <ul className='mt-6 space-y-3 border-t border-stone pt-5'>
                                            {area.places.map(place => (
                                                <li key={place} className='flex gap-3 text-sm leading-snug text-charcoal/85'>
                                                    <span aria-hidden='true' className='mt-[0.45rem] size-1 shrink-0 rounded-full bg-forest' />
                                                    {place}
                                                </li>
                                            ))}
                                        </ul>
                                    </article>
                                </Reveal>
                            </li>
                        )
                    })}
                </ul>

                <p className='mt-8 text-xs leading-relaxed text-muted-foreground'>
                    Nearby schools, hospitals and centres are listed as a guide to the area and are not affiliated with Rockwood Estate.
                </p>
            </div>
        </section>
    )
}
